/**
 * ADR 379 — the panel is page-aware: what it suggests and which open alerts it
 * shows depend on where the user is. Suggestions are i18n keys
 * (`assistant.q.<key>`), and every one of them is a question a tool answers
 * TODAY — a chip that leads to "não há motor para isso" is a broken promise.
 */
export type AlertCode =
  | "IMPACT" | "OFFLINE" | "LINK_DOWN"
  | "RUN_COVERAGE_LOW" | "AISLE_AGREEMENT_LOW" | "VERTICAL_HEIGHT_MISSING";

export interface PageHints {
  /** i18n key suffix for the scope line (`assistant.scope.<scope>`). */
  scope: string;
  /** i18n key suffixes for the suggestion chips (`assistant.q.<key>`). */
  questions: string[];
  /** Open alerts of these codes are "about this page"; `"all"` on /alerts. */
  alertCodes: AlertCode[] | "all";
}

const INVENTORY_ALERTS: AlertCode[] = ["RUN_COVERAGE_LOW", "AISLE_AGREEMENT_LOW", "VERTICAL_HEIGHT_MISSING"];
const ASSET_ALERTS: AlertCode[] = ["IMPACT", "OFFLINE", "LINK_DOWN"];

export function pageHints(pathname: string, opts: { liveCount?: boolean } = {}): PageHints {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p.startsWith("/live")) {
    return {
      scope: "live",
      questions: [
        ...(opts.liveCount ? ["liveCountEta"] : []),
        "divergencesByKind", "coverageByAisle", "wherePallet", "velocityTop",
      ],
      alertCodes: [...INVENTORY_ALERTS, "IMPACT"],
    };
  }
  if (p.startsWith("/inventory/runs/")) {
    return { scope: "run", questions: ["divergencesByKind", "coverageByAisle", "wherePallet", "agedStock"], alertCodes: INVENTORY_ALERTS };
  }
  if (p.startsWith("/inventory")) {
    return { scope: "inventory", questions: ["velocityTop", "curveValidity", "abcConcentration", "slotting", "agedStock"], alertCodes: INVENTORY_ALERTS };
  }
  if (p.startsWith("/fleet") || p.startsWith("/admin/assets") || p.startsWith("/safety")) {
    return { scope: "fleet", questions: ["impactsOpen", "assetsOffline", "alertsByKind"], alertCodes: ASSET_ALERTS };
  }
  if (p.startsWith("/network")) {
    return { scope: "network", questions: ["linksDown", "alertsByKind"], alertCodes: ["LINK_DOWN", "OFFLINE"] };
  }
  if (p.startsWith("/alerts")) {
    return { scope: "alerts", questions: ["alertsByKind", "impactsOpen", "coverageAlerts"], alertCodes: "all" };
  }
  if (p.startsWith("/oee")) {
    return { scope: "oee", questions: ["alertsByKind", "assetsOffline"], alertCodes: ASSET_ALERTS };
  }
  return { scope: "general", questions: ["divergencesByKind", "velocityTop", "alertsByKind"], alertCodes: "all" };
}

/** The open alerts that belong on this page, newest first, capped. */
export function relatedAlerts<T extends { code: string; ts: string }>(alerts: T[], hints: PageHints, max = 5): T[] {
  const keep = hints.alertCodes === "all" ? null : new Set<string>(hints.alertCodes);
  return alerts
    .filter((a) => keep === null || keep.has(a.code))
    .sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : 0))
    .slice(0, max);
}
